import { call, put, select, race, take, cancelled } from 'redux-saga/effects'
import { selectUser } from './user'
import axios from 'axios'
import API from '../config'
import { get } from 'lodash-es'
import { normalize } from 'normalizr'

import { requestCollections, requestSaved } from './userContent'
import { requestAccountPicks } from './pick'
import { setReferrerId } from './referrer'

import { user as userSchema } from '../schemas'
import { getHeaders } from './api'

// ------------------------------------
// Constants
// ------------------------------------
const rootApi = `${API.apiPath}/${API.apiVersion}/`

export const REQUEST_PROFILE = 'REQUEST_PROFILE'
export const RECEIVE_PROFILE = 'RECEIVE_PROFILE'

export const REQUEST_USER_ID = 'REQUEST_USER_ID'
export const SET_USER_ID = 'SET_USER_ID'
export const USER_DOES_NOT_EXIST = 'USER_DOES_NOT_EXIST'

export const REQUEST_OTHERS_INFO = 'REQUEST_OTHERS_INFO'
export const SET_OTHERS_INFO = 'SET_OTHERS_INFO'

export const REQUEST_POINTS_DETAILS = 'REQUEST_POINTS_DETAILS'
export const RECEIVE_POINTS_DETAILS = 'RECEIVE_POINTS_DETAILS'

export const FOLLOW_USER = 'FOLLOW_USER'

export const UNFOLLOW_USER = 'UNFOLLOW_USER'
export const USER_UNFOLLOWED = 'USER_UNFOLLOWED'

const REMOVE_PROFILE = 'REMOVE_PROFILE'

// ------------------------------------
// Actions
// ------------------------------------
export function requestProfile(id) {
  return { type: REQUEST_PROFILE, id }
}

export function receiveProfile(data) {
  return { type: RECEIVE_PROFILE, payload: { data } }
}

function userDoesNotExist(data) {
  return { type: USER_DOES_NOT_EXIST, payload: { data } }
}

function* getProfile(action) {
  const authedUrl = rootApi + `account/get_profile`
  const unauthedUrl = rootApi + `account/get_public_profile`
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const CancelToken = axios.CancelToken
  const source = CancelToken.source()
  const headers = getHeaders(token)

  try {
    const response = yield call(
      [axios, 'post'],
      token ? authedUrl : unauthedUrl,
      {
        user_id: get(action, 'id') || get(user, 'data.user.id')
      },
      {
        headers,
        cancelToken: source.token
      }
    )
    const normalizedResponse = normalize(response.data, userSchema)
    yield put(receiveProfile(normalizedResponse))
  } catch (e) {
  } finally {
    if (yield cancelled()) {
      source.cancel()
    }
  }
}

export function requestPointsDetails() {
  return { type: REQUEST_POINTS_DETAILS }
}

function receivePointsDetails(payload) {
  return { type: RECEIVE_POINTS_DETAILS, payload }
}

function* getPointsDetails() {
  const url = rootApi + `account/get_points`
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const CancelToken = axios.CancelToken
  const source = CancelToken.source()

  try {
    const headers = getHeaders(token)
    const response = yield call(
      [axios, 'post'],
      url,
      {
        user_id: user.data.user.id
      },
      {
        headers,
        cancelToken: source.token
      }
    )
    yield put(receivePointsDetails({ ...response.data, userId: user.data.user.id }))
  } catch (e) {
  } finally {
    if (yield cancelled()) {
      source.cancel()
    }
  }
}

export function removeProfile() {
  return { type: REMOVE_PROFILE }
}

export function setUserId(id) {
  return { type: SET_USER_ID, id }
}

export function requestUserId(username, feeds, refId, isReferrer) {
  return {
    type: REQUEST_USER_ID,
    payload: {
      username,
      feeds,
      refId,
      isReferrer
    }
  }
}

function* getUserId(action) {
  const url = rootApi + `account/does_user_exist`
  const CancelToken = axios.CancelToken
  const source = CancelToken.source()
  const { feeds, refId, isReferrer } = action.payload
  const username = decodeURI(action.payload.username.replace('@', ''))

  try {
    const response = yield call(
      [axios, 'post'],
      url,
      {
        username
      },
      {
        headers: getHeaders(),
        cancelToken: source.token
      }
    )
    if (!response.data || !response.data.result) {
      yield put(userDoesNotExist([username, 'user_not_found']))
    } else {
      const userId = response.data.user_id

      yield put(setUserId(userId))
      yield put(requestProfile(userId))
      if (isReferrer) {
        yield put(setReferrerId({ id: userId }))
      }
      if (!feeds) {
        yield put(requestAccountPicks(userId))
        yield put(requestCollections({ id: userId, published: 1 }))
      } else {
        if (feeds.includes('picks')) {
          yield put(requestAccountPicks(userId, null, { refId }))
        }
        if (feeds.includes('collections')) {
          yield put(requestCollections({ id: userId, published: 1, refId }))
        }
        if (feeds.includes('saved')) {
          yield put(requestSaved({ id: userId, published: 1, refId }))
        }
      }
    }
  } catch (e) {
  } finally {
    if (yield cancelled()) {
      source.cancel()
    }
  }
}

export const requestOthersInfo = () => {
  return { type: REQUEST_OTHERS_INFO }
}

const setOthersInfo = data => {
  return { type: SET_OTHERS_INFO, payload: { data } }
}

function* getOthersInfo() {
  const url = rootApi + `account/profile-others-count`
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const CancelToken = axios.CancelToken
  const source = CancelToken.source()

  try {
    const response = yield call([axios, 'get'], url, {
      headers: getHeaders(token),
      cancelToken: source.token
    })
    const normalizedData = normalize({ ...response.data, id: user.data.user.id }, userSchema)
    yield put(setOthersInfo(normalizedData))
  } catch (e) {
  } finally {
    if (yield cancelled()) {
      source.cancel()
    }
  }
}

export function followUser(id) {
  return { type: FOLLOW_USER, id }
}

function* postFollowing(action) {
  const url = rootApi + `account/follow_user`
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  try {
    const response = yield call(
      [axios, 'post'],
      url,
      {
        following_id: action.id
      },
      {
        headers: getHeaders(token)
      }
    )
    const normalizedResponse = normalize(response.data, userSchema)
    yield put(receiveProfile(normalizedResponse))
  } catch (e) {
  } finally {
  }
}

export function unfollowUser(id) {
  return { type: UNFOLLOW_USER, id }
}

function userUnfollowed({ userId, profileId }) {
  return { type: USER_UNFOLLOWED, payload: { userId, profileId } }
}

function* postUnfollowing(action) {
  const url = rootApi + `account/unfollow_user`
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  try {
    const response = yield call(
      [axios, 'post'],
      url,
      {
        following_id: action.id
      },
      {
        headers: getHeaders(token)
      }
    )
    yield put(userUnfollowed({ userId: user.data.user.id, profileId: response.data.user_info.user_id }))
  } catch (e) {
  } finally {
  }
}

function* checkProfileSaga() {
  while (true) {
    const action = yield take(REQUEST_USER_ID)
    yield race({
      task: call(getUserId, action),
      cancel: take('RESET_PROFILE')
    })
  }
}

function* getProfileSaga() {
  while (true) {
    const action = yield take(REQUEST_PROFILE)
    yield race({
      task: call(getProfile, action),
      cancel: take('RESET_PROFILE')
    })
  }
}

function* getPointsSaga() {
  while (true) {
    yield take(REQUEST_POINTS_DETAILS)
    yield race({
      task: call(getPointsDetails),
      cancel: take('RESET_PROFILE')
    })
  }
}

function* getOthersInfoSaga() {
  while (true) {
    yield take(REQUEST_OTHERS_INFO)
    yield race({
      task: call(getOthersInfo),
      cancel: take('RESET_PROFILE')
    })
  }
}

function* postFollowingSaga() {
  while (true) {
    const action = yield take(FOLLOW_USER)
    yield race({
      task: call(postFollowing, action),
      cancel: take('LOCATION_CHANGE')
    })
  }
}

function* postUnfollowingSaga() {
  while (true) {
    const action = yield take(UNFOLLOW_USER)
    yield race({
      task: call(postUnfollowing, action),
      cancel: take('LOCATION_CHANGE')
    })
  }
}

export const sagas = [
  checkProfileSaga,
  getProfileSaga,
  getPointsSaga,
  getOthersInfoSaga,
  postFollowingSaga,
  postUnfollowingSaga
]

// ------------------------------------
// Reducer
// ------------------------------------
const initialState = {
  list: []
}

export default function(state = initialState, action) {
  switch (action.type) {
    case SET_USER_ID: {
      state.list.push(action.id)
      return { ...state }
    }
    case REMOVE_PROFILE: {
      state.list.pop()
      return { ...state }
    }
  }

  return state
}
